import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  buildExecutionRunSharedFilters,
  buildSelectedDayFilters,
} from '@/features/executionRuns/hooks/useExecutionRunsPageViewModel.filters';
import { useExecutionRunStatusMetrics } from '@/features/executionRuns/hooks/useExecutionRunStatusMetrics';
import { buildChartDates, buildLineChartSeries } from '@/features/executions/utils/chartData';
import { buildStackedExecutionChartData } from '@/features/executions/utils/stackedExecutionChart';
import { buildMetricValueItems } from '@/features/metrics/components/metricValueItems';
import { useMetricsQuery } from '@/features/metrics/hooks/metrics';
import { buildThresholdGuideLines } from '@/features/metrics/lib/chartThresholdGuides';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { type QualityRange, useQualityDateRange } from '@/features/quality/hooks';
import { useResultsFileUpload } from '@/features/quality/sections/useResultsFileUpload';
import { collapseGroupedStackedChartData } from '@/features/shared/hooks/useEntityTrendsPage.statusPresentation';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { useScopedProjectFilter } from '@/features/shared/hooks/useScopedProjectFilter';
import {
  collapseStatusOptions,
  expandStatusFilterValues,
} from '@/features/shared/utils/projectStatusPresentation';
import { suiteRunStatusOptions } from '@/shared/constants/statusOptions';
import { useIsAuthenticated } from '@/shared/hooks/auth';
import { usePersistedStringArray } from '@/shared/hooks/usePersistedStringArray';
import { useTagOptions } from '@/shared/hooks/useTagOptions';
import { resolveVisibleMetricThresholdKeys } from '@/shared/utils/metricsThresholds';
import {
  buildDateRangeFilters,
  buildContainsFilter,
  buildEqFilter,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type {
  ExecutionRunsChartTab,
  ExecutionRunsPageTabKey,
  UseExecutionRunsPageViewModelResult,
} from '@/features/executionRuns/hooks/useExecutionRunsPageViewModel.types';
import type { SuiteRunStatus } from '@flowqa/shared';

export const useExecutionRunsPageViewModel = (): UseExecutionRunsPageViewModelResult => {
  const location = useLocation();
  const { selectedOrganisationId } = useOrganisationFilter();
  const {
    effectiveProjectId,
    handleProjectsChange,
    projectFilterOptions,
    projectOptions,
    selectedProjects,
    showProjectFilter,
  } = useScopedProjectFilter({ enabled: true });
  const { thresholds, isLoading: thresholdsLoading } =
    useProjectMetricThresholds(effectiveProjectId);
  const isAuthenticated = useIsAuthenticated();
  const [range, setRangeState] = useState<QualityRange>('7d');
  const [activeTab, setActiveTabState] = useState<ExecutionRunsPageTabKey>('execution-runs');
  const [chartTab, setChartTabState] = useState<ExecutionRunsChartTab>('status');
  const [suppressChartAnimation, setSuppressChartAnimation] = useState(false);
  const [searchValue, setSearchValueState] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<SuiteRunStatus[]>([]);
  const [selectedTags, setSelectedTags] = usePersistedStringArray('tag-filter:execution-runs');
  const [selectedStartDay, setSelectedStartDay] = useState<string | null>(null);
  const [selectedEndDay, setSelectedEndDay] = useState<string | null>(null);
  const [chartSelectedStartDay, setChartSelectedStartDay] = useState<string | null>(null);
  const [chartSelectedEndDay, setChartSelectedEndDay] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [hasUploadRefresh, setHasUploadRefresh] = useState(false);
  const { groupErroredWithFailed, statusPresentationLoading } = useProjectStatusPresentation();
  const executionDataQueriesEnabled = isAuthenticated && !statusPresentationLoading;
  const isLatestMode = range === 'latest';
  const { rangeStartIso, rangeEndIso, previousRangeStartIso, previousRangeEndIso } =
    useQualityDateRange(range);
  const dateFiltersEnabled = true;
  const uploadProjectIdFieldId = useId();
  useEffect(() => {
    setHasUploadRefresh(false);
  }, [effectiveProjectId, selectedOrganisationId]);
  const handleUploadRefresh = useCallback(() => {
    setHasUploadRefresh(true);
  }, []);
  const uploadFlow = useResultsFileUpload({
    selectedProjectId: effectiveProjectId,
    selectedOrganisationId,
    onRefresh: handleUploadRefresh,
  });
  const { tagOptions, loading: tagOptionsLoading } = useTagOptions({
    projectId: effectiveProjectId,
    enabled: isAuthenticated && hasLoaded,
  });

  useEffect(() => {
    if (location.hash === '#uploads') {
      setActiveTabState('uploads');
      const anchor = document.getElementById('uploads-table');
      if (anchor) {
        anchor.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
      return;
    }
    if (location.hash === '#results-files') {
      setActiveTabState('results-files');
      const anchor = document.getElementById('results-files-table');
      if (anchor) {
        anchor.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }
  }, [location.hash]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);
    return () => {
      window.clearTimeout(timer);
    };
  }, [searchValue]);

  useEffect(() => {
    setSuppressChartAnimation(true);
    const timer = window.setTimeout(() => {
      setSuppressChartAnimation(false);
    }, 0);
    return () => {
      window.clearTimeout(timer);
    };
  }, [chartTab]);

  const uploadDateRangeFilters = useMemo(
    () => buildDateRangeFilters('createdAt', rangeStartIso, rangeEndIso),
    [rangeEndIso, rangeStartIso],
  );
  const executionRunDateRangeFilters = useMemo(
    () => buildDateRangeFilters('startedAt', rangeStartIso, rangeEndIso),
    [rangeEndIso, rangeStartIso],
  );
  const selectedUploadDayFilters = useMemo(
    () =>
      buildSelectedDayFilters({
        field: 'createdAt',
        selectedStartDay,
        selectedEndDay,
      }),
    [selectedEndDay, selectedStartDay],
  );
  const selectedExecutionRunDayFilters = useMemo(
    () =>
      buildSelectedDayFilters({
        field: 'startedAt',
        selectedStartDay,
        selectedEndDay,
      }),
    [selectedEndDay, selectedStartDay],
  );
  const tagFilters = useMemo(() => buildInFilter('tags', selectedTags), [selectedTags]);
  const effectiveSelectedStatuses = useMemo(
    () => expandStatusFilterValues(selectedStatuses, groupErroredWithFailed),
    [groupErroredWithFailed, selectedStatuses],
  );
  const projectFilters = useMemo(
    () =>
      showProjectFilter
        ? buildInFilter('projectId', selectedProjects)
        : buildEqFilter('projectId', effectiveProjectId),
    [effectiveProjectId, selectedProjects, showProjectFilter],
  );
  const executionRunSearchFilters = useMemo(
    () => buildContainsFilter('name', debouncedSearchValue),
    [debouncedSearchValue],
  );
  const uploadScopedTableFilters = useMemo(
    () =>
      mergeFilters(selectedUploadDayFilters, uploadDateRangeFilters, tagFilters, projectFilters),
    [projectFilters, selectedUploadDayFilters, uploadDateRangeFilters, tagFilters],
  );
  const executionRunTableFilters = useMemo(
    () =>
      buildExecutionRunSharedFilters({
        selectedExecutionRunDayFilters,
        executionRunDateRangeFilters,
        executionRunSearchFilters,
        selectedStatuses: effectiveSelectedStatuses,
        tagFilters,
        projectFilters,
      }),
    [
      executionRunDateRangeFilters,
      executionRunSearchFilters,
      effectiveSelectedStatuses,
      projectFilters,
      selectedExecutionRunDayFilters,
      tagFilters,
    ],
  );
  const executionRunStatusOptionFilters = useMemo(
    () =>
      buildExecutionRunSharedFilters({
        selectedExecutionRunDayFilters,
        executionRunDateRangeFilters,
        executionRunSearchFilters,
        selectedStatuses: [],
        tagFilters,
        projectFilters,
      }),
    [
      executionRunDateRangeFilters,
      executionRunSearchFilters,
      projectFilters,
      selectedExecutionRunDayFilters,
      tagFilters,
    ],
  );
  const executionRunSummaryFilters = executionRunTableFilters;
  const previousExecutionRunDateRangeFilters = useMemo(
    () =>
      previousRangeStartIso && previousRangeEndIso
        ? buildDateRangeFilters('startedAt', previousRangeStartIso, previousRangeEndIso)
        : undefined,
    [previousRangeEndIso, previousRangeStartIso],
  );
  const previousExecutionRunSummaryFilters = useMemo(
    () =>
      previousExecutionRunDateRangeFilters
        ? mergeFilters(
            previousExecutionRunDateRangeFilters,
            executionRunSearchFilters,
            buildInFilter('status', effectiveSelectedStatuses),
            tagFilters,
            projectFilters,
          )
        : undefined,
    [
      executionRunSearchFilters,
      effectiveSelectedStatuses,
      previousExecutionRunDateRangeFilters,
      projectFilters,
      tagFilters,
    ],
  );
  const hasCustomDates = Boolean(selectedStartDay ?? selectedEndDay);

  useEffect(() => {
    if (!isLatestMode) {
      setChartSelectedStartDay(null);
      setChartSelectedEndDay(null);
    }
  }, [isLatestMode]);

  const {
    executionRunMetricCards,
    loading: executionRunMetricsLoading,
    counts: executionRunCounts,
    total: executionRunTotal,
    previousCounts: previousExecutionRunCounts,
    previousTotal: previousExecutionRunTotal,
  } = useExecutionRunStatusMetrics({
    organisationId: selectedOrganisationId ?? undefined,
    projectId: effectiveProjectId,
    baseFilters: executionRunSummaryFilters,
    previousFilters: previousExecutionRunSummaryFilters,
    enabled: executionDataQueriesEnabled,
  });
  const { counts: executionRunStatusOptionCounts } = useExecutionRunStatusMetrics({
    organisationId: selectedOrganisationId ?? undefined,
    projectId: effectiveProjectId,
    baseFilters: executionRunStatusOptionFilters,
    enabled: executionDataQueriesEnabled,
  });
  const statusOptions = useMemo(
    () =>
      collapseStatusOptions(suiteRunStatusOptions, groupErroredWithFailed).map((option) => ({
        ...option,
        count: executionRunStatusOptionCounts[option.value] ?? 0,
      })),
    [executionRunStatusOptionCounts, groupErroredWithFailed],
  );

  const metricsRange = useMemo(
    () => (hasCustomDates ? 'all' : isLatestMode ? 'all' : range),
    [hasCustomDates, isLatestMode, range],
  );
  const previousStartDay = previousRangeStartIso?.slice(0, 10) ?? null;
  const previousEndDay = previousRangeEndIso?.slice(0, 10) ?? null;
  const showMetricDelta = Boolean(
    !isLatestMode && range !== 'all' && !hasCustomDates && previousStartDay && previousEndDay,
  );
  const executionMetricsQuery = useMetricsQuery({
    range: metricsRange,
    statuses: selectedStatuses,
    tags: selectedTags,
    scope: 'executionRun',
    projectId: effectiveProjectId,
    name: debouncedSearchValue || null,
    startDay: isLatestMode ? null : hasCustomDates ? selectedStartDay : null,
    endDay: isLatestMode ? null : hasCustomDates ? selectedEndDay : null,
    latestOnly: isLatestMode,
    enabled: executionDataQueriesEnabled,
    preservePreviousData: true,
    refetchOnWindowFocus: false,
  });
  const previousMetricsQuery = useMetricsQuery({
    range: 'all',
    statuses: selectedStatuses,
    tags: selectedTags,
    scope: 'executionRun',
    projectId: effectiveProjectId,
    name: debouncedSearchValue || null,
    startDay: showMetricDelta ? previousStartDay : null,
    endDay: showMetricDelta ? previousEndDay : null,
    enabled: executionDataQueriesEnabled && showMetricDelta && executionMetricsQuery.isFetched,
    preservePreviousData: true,
    refetchOnWindowFocus: false,
  });
  const passRateOverride = useMemo(() => {
    if (executionRunMetricsLoading) return null;
    const total =
      executionRunTotal ??
      Object.values(executionRunCounts).reduce((sum, value) => {
        return sum + (typeof value === 'number' ? value : 0);
      }, 0);
    if (total <= 0) return 0;
    const passed = executionRunCounts.passed ?? 0;
    return (passed / total) * 100;
  }, [executionRunCounts, executionRunMetricsLoading, executionRunTotal]);
  const previousPassRateOverride = useMemo(() => {
    if (!showMetricDelta || executionRunMetricsLoading) return null;
    const total =
      previousExecutionRunTotal ??
      Object.values(previousExecutionRunCounts).reduce((sum, value) => {
        return sum + (typeof value === 'number' ? value : 0);
      }, 0);
    if (total <= 0) return 0;
    const passed = previousExecutionRunCounts.passed ?? 0;
    return (passed / total) * 100;
  }, [
    executionRunMetricsLoading,
    previousExecutionRunCounts,
    previousExecutionRunTotal,
    showMetricDelta,
  ]);
  const visibleMetricKeys = useMemo(
    () => (thresholdsLoading ? [] : resolveVisibleMetricThresholdKeys(thresholds.executionRuns)),
    [thresholds.executionRuns, thresholdsLoading],
  );
  const metricValueItems = useMemo(
    () =>
      buildMetricValueItems({
        metrics: executionMetricsQuery.data ?? null,
        previousMetrics: showMetricDelta ? (previousMetricsQuery.data ?? null) : null,
        thresholds: thresholds.executionRuns,
        passRateOverride,
        previousPassRateOverride,
        showDelta: showMetricDelta,
        visibleMetricKeys,
      }),
    [
      executionMetricsQuery.data,
      previousMetricsQuery.data,
      passRateOverride,
      previousPassRateOverride,
      showMetricDelta,
      thresholds.executionRuns,
      visibleMetricKeys,
    ],
  );
  const stackedChartData = useMemo(() => {
    const baseData = buildStackedExecutionChartData(executionMetricsQuery.data);
    return groupErroredWithFailed ? collapseGroupedStackedChartData(baseData) : baseData;
  }, [executionMetricsQuery.data, groupErroredWithFailed]);
  const chartDates = useMemo(() => {
    const dates = executionMetricsQuery.data?.metricsRangeDates ?? [];
    return buildChartDates(dates, dates);
  }, [executionMetricsQuery.data?.metricsRangeDates]);
  const chartFilterRange = useMemo(() => {
    if (!isLatestMode || !chartSelectedStartDay) return null;
    return {
      start: chartSelectedStartDay,
      end: chartSelectedEndDay ?? chartSelectedStartDay,
    };
  }, [chartSelectedEndDay, chartSelectedStartDay, isLatestMode]);
  const chartFilterIndices = useMemo(() => {
    if (!chartFilterRange) return null;
    const { start, end } = chartFilterRange;
    return chartDates
      .map((date, index) => (date >= start && date <= end ? index : -1))
      .filter((index) => index >= 0);
  }, [chartDates, chartFilterRange]);
  const metricsStartDay = useMemo(() => chartDates[0] ?? null, [chartDates]);
  const metricsEndDay = useMemo(
    () => (chartDates.length > 0 ? (chartDates[chartDates.length - 1] ?? null) : null),
    [chartDates],
  );
  const pickerStartDay = useMemo(
    () =>
      selectedStartDay ??
      (range !== 'latest' && range !== 'all'
        ? (rangeStartIso?.slice(0, 10) ?? null)
        : metricsStartDay),
    [metricsStartDay, range, rangeStartIso, selectedStartDay],
  );
  const pickerEndDay = useMemo(
    () =>
      selectedEndDay ??
      (range !== 'latest' && range !== 'all' ? (rangeEndIso?.slice(0, 10) ?? null) : metricsEndDay),
    [metricsEndDay, range, rangeEndIso, selectedEndDay],
  );
  const handleStartDayChange = useCallback(
    (nextDay: string | null) => {
      setSelectedStartDay(nextDay);
      if (!nextDay) {
        setSelectedEndDay(null);
        return;
      }
      if (!selectedEndDay) {
        setSelectedEndDay(pickerEndDay ?? nextDay);
      }
    },
    [pickerEndDay, selectedEndDay],
  );
  const handleEndDayChange = useCallback(
    (nextDay: string | null) => {
      setSelectedEndDay(nextDay);
      if (!nextDay) {
        setSelectedStartDay(null);
        return;
      }
      if (!selectedStartDay) {
        setSelectedStartDay(pickerStartDay ?? nextDay);
      }
    },
    [pickerStartDay, selectedStartDay],
  );
  const handleChartSelect = useCallback(
    (dataIndex: number, _seriesKey?: string) => {
      const day = chartDates[dataIndex];
      if (!day) return;
      if (isLatestMode) {
        if (chartSelectedStartDay) {
          setChartSelectedStartDay(null);
          setChartSelectedEndDay(null);
          setSelectedStartDay(null);
          setSelectedEndDay(null);
        } else {
          setChartSelectedStartDay(day);
          setChartSelectedEndDay(day);
          setSelectedStartDay(day);
          setSelectedEndDay(day);
        }
      } else {
        const isSameDay = selectedStartDay === day && selectedEndDay === day;
        if (isSameDay) {
          setSelectedStartDay(null);
          setSelectedEndDay(null);
        } else {
          setSelectedStartDay(day);
          setSelectedEndDay(day);
        }
      }
    },
    [
      chartDates,
      chartSelectedEndDay,
      chartSelectedStartDay,
      isLatestMode,
      selectedEndDay,
      selectedStartDay,
    ],
  );
  const hasChartDateFilter = Boolean(selectedStartDay ?? selectedEndDay);
  const clearChartDateFilter = useCallback(() => {
    setChartSelectedStartDay(null);
    setChartSelectedEndDay(null);
    setSelectedStartDay(null);
    setSelectedEndDay(null);
  }, []);

  const chartHeight = 360;
  const showChartSkeleton =
    executionMetricsQuery.isFetching &&
    executionMetricsQuery.data === undefined &&
    !executionMetricsQuery.isError;
  const lineChartLabels = executionMetricsQuery.data?.metricsRangeLabels ?? [];
  const passRateChartData = useMemo(() => {
    const series = buildLineChartSeries(
      executionMetricsQuery.data?.series.filteredPassRate,
      lineChartLabels,
      chartDates,
      'passRate',
    );
    if (series) return series;
    return executionMetricsQuery.isFetched ? [] : null;
  }, [
    chartDates,
    executionMetricsQuery.data?.series.filteredPassRate,
    executionMetricsQuery.isFetched,
    lineChartLabels,
  ]);
  const frequencyChartData = useMemo(() => {
    const series = buildLineChartSeries(
      executionMetricsQuery.data?.series.executionTrendMetrics,
      lineChartLabels,
      chartDates,
      'frequency',
    );
    if (series) return series;
    return executionMetricsQuery.isFetched ? [] : null;
  }, [
    chartDates,
    executionMetricsQuery.data?.series.executionTrendMetrics,
    executionMetricsQuery.isFetched,
    lineChartLabels,
  ]);
  const durationChartData = useMemo(() => {
    const series = buildLineChartSeries(
      executionMetricsQuery.data?.series.executionDurationAverage,
      lineChartLabels,
      chartDates,
      'duration',
    );
    if (series) return series;
    return executionMetricsQuery.isFetched ? [] : null;
  }, [
    chartDates,
    executionMetricsQuery.data?.series.executionDurationAverage,
    executionMetricsQuery.isFetched,
    lineChartLabels,
  ]);
  const filteredPassRateChartData = useMemo(() => {
    if (!passRateChartData || !chartFilterIndices) return passRateChartData;
    return chartFilterIndices.map((index) => passRateChartData[index]).filter(Boolean);
  }, [chartFilterIndices, passRateChartData]);
  const filteredFrequencyChartData = useMemo(() => {
    if (!frequencyChartData || !chartFilterIndices) return frequencyChartData;
    return chartFilterIndices.map((index) => frequencyChartData[index]).filter(Boolean);
  }, [chartFilterIndices, frequencyChartData]);
  const filteredDurationChartData = useMemo(() => {
    if (!durationChartData || !chartFilterIndices) return durationChartData;
    return chartFilterIndices.map((index) => durationChartData[index]).filter(Boolean);
  }, [chartFilterIndices, durationChartData]);
  const passRateGuideLines = useMemo(
    () => buildThresholdGuideLines(thresholds.executionRuns.passRate),
    [thresholds.executionRuns.passRate],
  );
  const frequencyGuideLines = useMemo(
    () => buildThresholdGuideLines(thresholds.executionRuns.frequency),
    [thresholds.executionRuns.frequency],
  );
  const durationGuideLines = useMemo(
    () => buildThresholdGuideLines(thresholds.executionRuns.avgDuration),
    [thresholds.executionRuns.avgDuration],
  );
  const showLineChartPoints = useMemo(() => {
    const start = isLatestMode ? chartSelectedStartDay : selectedStartDay;
    const end = isLatestMode ? (chartSelectedEndDay ?? chartSelectedStartDay) : selectedEndDay;
    return Boolean(start && end && start === end);
  }, [chartSelectedEndDay, chartSelectedStartDay, isLatestMode, selectedEndDay, selectedStartDay]);
  const filteredStackedChartData = useMemo(() => {
    if (!stackedChartData || !chartFilterIndices) return stackedChartData;
    const filteredData = chartFilterIndices
      .map((index) => stackedChartData.data[index])
      .filter(Boolean);
    return { ...stackedChartData, data: filteredData };
  }, [chartFilterIndices, stackedChartData]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!executionRunMetricsLoading && executionMetricsQuery.isFetched) {
      setHasLoaded(true);
    }
  }, [executionMetricsQuery.isFetched, executionRunMetricsLoading, isAuthenticated]);

  const setRange = useCallback((value: QualityRange) => {
    setRangeState(value);
    setSelectedStartDay(null);
    setSelectedEndDay(null);
    setChartSelectedStartDay(null);
    setChartSelectedEndDay(null);
  }, []);

  const setSearchValue = useCallback((value: string) => {
    setSearchValueState(value);
  }, []);

  const setActiveTab = useCallback((tab: ExecutionRunsPageTabKey) => {
    setActiveTabState(tab);
  }, []);

  const setChartTab = useCallback((tab: ExecutionRunsChartTab) => {
    setChartTabState(tab);
  }, []);

  const onStatusToggle = useCallback((status: SuiteRunStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status],
    );
  }, []);
  const onStatusesChange = useCallback((nextStatuses: SuiteRunStatus[]) => {
    setSelectedStatuses(nextStatuses);
  }, []);

  return {
    range,
    setRange,
    searchValue,
    setSearchValue,
    activeTab,
    setActiveTab,
    selectedStatuses,
    statusOptions,
    selectedTags,
    selectedProjects,
    projectFilterOptions,
    showProjectFilter,
    tagOptions,
    tagOptionsLoading,
    onStatusToggle,
    onStatusesChange,
    onTagsChange: setSelectedTags,
    onProjectsChange: handleProjectsChange,
    executionRunMetricCards,
    executionRunMetricsLoading,
    metricValueItems,
    visibleMetricKeys,
    dateFiltersEnabled,
    selectedProjectId: effectiveProjectId,
    chart: {
      tab: chartTab,
      setTab: setChartTab,
      height: chartHeight,
      showSkeleton: showChartSkeleton,
      stackedData: filteredStackedChartData ?? null,
      passRateData: filteredPassRateChartData ?? null,
      frequencyData: filteredFrequencyChartData ?? null,
      durationData: filteredDurationChartData ?? null,
      passRateGuideLines,
      frequencyGuideLines,
      durationGuideLines,
      suppressAnimation: suppressChartAnimation,
      showPoints: showLineChartPoints,
      hasDateFilter: hasChartDateFilter,
      clearDateFilter: clearChartDateFilter,
      onSelect: handleChartSelect,
    },
    datePicker: {
      isLatestMode,
      hasCustomDates,
      startDay: pickerStartDay,
      endDay: pickerEndDay,
      onStartDayChange: handleStartDayChange,
      onEndDayChange: handleEndDayChange,
    },
    filters: {
      executionRuns: executionRunTableFilters,
      resultsFiles: uploadScopedTableFilters,
      uploads: uploadScopedTableFilters,
    },
    uploadDialog: {
      hasUploadRefresh,
      uploadOpen: uploadFlow.uploadOpen,
      uploadMutation: uploadFlow.uploadMutation,
      disableUpload: uploadFlow.disableUpload,
      uploadProjectId: uploadFlow.uploadProjectId,
      uploadTagsInput: uploadFlow.uploadTagsInput,
      uploadTags: uploadFlow.uploadTags,
      uploadTagColors: uploadFlow.uploadTagColors,
      uploadTagValues: uploadFlow.uploadTagValues,
      uploadDuplicatePolicy: uploadFlow.uploadDuplicatePolicy,
      uploadProjectIdFieldId,
      uploadFiles: uploadFlow.uploadFiles,
      selectedFiles: uploadFlow.selectedFiles,
      uploadError: uploadFlow.uploadError,
      selectedUploadFiles: uploadFlow.selectedUploadFiles,
      uploadRunEstimate: uploadFlow.uploadRunEstimate,
      isSupportedFile: uploadFlow.isSupportedFile,
      csvHeaderStatusByName: uploadFlow.csvHeaderStatusByName,
      csvHeaderReasonByName: uploadFlow.csvHeaderReasonByName,
      fileValidationStatusByName: uploadFlow.fileValidationStatusByName,
      fileValidationReasonByName: uploadFlow.fileValidationReasonByName,
      handleFilesSelected: uploadFlow.handleFilesSelected,
      openUploadDialog: uploadFlow.openUploadDialog,
      closeUploadDialog: uploadFlow.closeUploadDialog,
      setUploadProjectId: uploadFlow.setUploadProjectId,
      setUploadTagsInput: uploadFlow.setUploadTagsInput,
      setUploadTags: uploadFlow.setUploadTags,
      setUploadTagColors: uploadFlow.setUploadTagColors,
      setUploadDuplicatePolicy: uploadFlow.setUploadDuplicatePolicy,
      setUploadFiles: uploadFlow.setUploadFiles,
      setSelectedFiles: uploadFlow.setSelectedFiles,
      setUploadError: uploadFlow.setUploadError,
      setCsvHeaderStatusByName: uploadFlow.setCsvHeaderStatusByName,
      setCsvHeaderReasonByName: uploadFlow.setCsvHeaderReasonByName,
      setFileValidationStatusByName: uploadFlow.setFileValidationStatusByName,
      setFileValidationReasonByName: uploadFlow.setFileValidationReasonByName,
      startUpload: uploadFlow.startUpload,
      projectOptions,
    },
  };
};
